import type { JSX } from 'react';
import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Chip } from '@heroui/react';

import { useDeclineInvitation, useInvitations } from '~/hooks/useInvitations';
import { logger } from '~/utils/logger';

/**
 * InvitationBanner displays pending organization invitations to the user.
 *
 * Shows at the top of the app when user has pending invitations.
 * Allows accepting/declining invitations without navigating to email.
 * Can be dismissed temporarily (will reappear on next page load if invitations remain).
 *
 * Story: COM-134 - Multi-org support (in-app invitation notifications)
 */
export function InvitationBanner(): JSX.Element | null {
  const { data: invitations, isLoading } = useInvitations();
  const declineMutation = useDeclineInvitation();
  const [dismissed, setDismissed] = useState(false);

  // Don't render if no invitations, loading, or dismissed
  if (isLoading || !invitations || invitations.length === 0 || dismissed) {
    return null;
  }

  // Show the first invitation (most recent)
  const invitation = invitations[0];
  const hasMultiple = invitations.length > 1;

  const handleView = (): void => {
    // Navigate to invitations settings page where user can manage all invitations
    window.location.href = '/settings/invitations';
  };

  const handleDecline = async (): Promise<void> => {
    logger.info({ component: 'InvitationBanner' }, 'Declining invitation from banner', {
      invitationId: invitation.id,
    });

    try {
      await declineMutation.mutateAsync(invitation.id);
      logger.info({ component: 'InvitationBanner' }, 'Invitation declined successfully');
    } catch (error) {
      logger.error(
        { component: 'InvitationBanner' },
        'Failed to decline invitation',
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  };

  const handleDismiss = (): void => {
    setDismissed(true);
  };

  return (
    <div
      className="relative bg-primary-600 text-white px-4 py-3 shadow-md"
      role="alert"
      data-testid="invitation-banner"
    >
      <div className="container mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="flex-1">
            <p className="font-semibold text-sm md:text-base">
              {invitation.source === 'domain_governance'
                ? `${invitation.organization_name} manages your email domain`
                : `You've been invited to join ${invitation.organization_name}`}
              {hasMultiple && (
                <Chip size="sm" className="ml-2" color="default">
                  +{invitations.length - 1} more
                </Chip>
              )}
            </p>
            <p className="text-xs md:text-sm text-primary-100 mt-1">
              {invitation.source === 'domain_governance'
                ? 'Click "View" to accept or decline membership.'
                : 'Click "View" to see details and accept, or check your email for the invitation link.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="solid"
            color="default"
            onPress={handleView}
            className="bg-white text-primary-600 font-semibold hover:bg-primary-50"
            aria-label="View invitation"
          >
            View
          </Button>
          <Button
            size="sm"
            variant="light"
            onPress={() => {
              void handleDecline();
            }}
            isLoading={declineMutation.isPending}
            className="text-white hover:bg-primary-700"
            aria-label="Decline invitation"
          >
            Decline
          </Button>
          <Button
            size="sm"
            onPress={handleDismiss}
            isIconOnly
            className="text-white hover:bg-primary-700"
            aria-label="Dismiss notification"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
